import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FileDownload from "js-file-download";
import { toast } from "react-toastify";
import documentsApiService from "../../../../services/documentsApiService";
import documentsAdapters from "../../../../adapters/documentsAdapters";
import tools from "../../../../helpers/tools";

// Aligné sur CustomPagination (itemsCountPerPage figé à 15).
const PER_PAGE = 15;

// Plafond de l'archive côté API (100 fichiers / 200 Mo). Il n'est pas rejoué
// ici — le serveur reste la source de vérité, y compris sur le volume, que le
// front ne connaît pas avant d'avoir chargé toutes les pages.
const MAX_SELECTION = 100;

const initialFilters = {
  immatriculation: "",
  immatriculations: [],
  clientUuid: "",
  catalogUuid: "",
  source: "",
  type: "",
  devisReference: "",
  numeroDossier: "",
  dateDebut: "",
  dateFin: "",
};

const initialMeta = {
  current_page: 1,
  per_page: PER_PAGE,
  total: 0,
  last_page: 1,
};

/**
 * LS-693 — liste transverse des documents d'un véhicule.
 *
 * `filtresVerrouilles` est imposé à chaque requête et n'est pas éditable dans
 * l'écran : c'est ce qui pré-filtre l'encart de la fiche véhicule de flotte sur
 * l'immatriculation du véhicule, sans dupliquer la mécanique de liste.
 */
const useDocuments = ({ filtresVerrouilles = {} } = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [meta, setMeta] = useState(initialMeta);
  const [isLoading, setIsLoading] = useState(false);
  // Distingue « l'API a répondu vide » de « l'API n'a pas répondu » : sans lui,
  // un échec de chargement s'afficherait comme un résultat sans documents.
  const [hasError, setHasError] = useState(false);
  // Le Loader plein écran est réservé au tout premier chargement : ensuite la
  // liste reste affichée, estompée, pendant les rechargements.
  const [aDejaCharge, setADejaCharge] = useState(false);
  const [selection, setSelection] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const debounceRef = useRef(null);
  // La frappe caractère par caractère fait se chevaucher les requêtes : seul le
  // résultat de la plus récente fait foi, une réponse lente partie avant est jetée.
  const sequenceRef = useRef(0);

  // Les verrous sont souvent construits en littéral par le parent : leur
  // référence change à chaque render alors que leur contenu est stable.
  // On dépend de leur sérialisation pour ne pas relancer la requête en boucle.
  const cleVerrous = JSON.stringify(filtresVerrouilles);

  const filtresEffectifs = useMemo(
    () => ({ ...filters, ...JSON.parse(cleVerrous) }),
    [filters, cleVerrous],
  );

  const fetchDocuments = useCallback(async () => {
    const sequence = ++sequenceRef.current;
    setIsLoading(true);
    try {
      const query = tools.getSearchString({
        ...documentsAdapters.toApi.filtresDocuments(filtresEffectifs),
        par_page: PER_PAGE,
        page,
      });
      const res = await documentsApiService.listerDocuments(query);
      if (sequence !== sequenceRef.current) return;
      const items = res.data?.data || [];
      setDocuments(items.map(documentsAdapters.fromApi.document));
      setMeta(res.data?.meta || { ...initialMeta, current_page: page });
      setHasError(false);
    } catch (err) {
      if (sequence !== sequenceRef.current) return;
      const message =
        err.response?.status === 403
          ? "Vous n'êtes pas autorisé à consulter les documents des véhicules."
          : "Erreur lors du chargement des documents.";
      toast.error(message);
      setDocuments([]);
      setMeta(initialMeta);
      setHasError(true);
    } finally {
      if (sequence === sequenceRef.current) {
        setIsLoading(false);
        setADejaCharge(true);
      }
    }
  }, [filtresEffectifs, page]);

  // Débounce (350 ms) sur tout changement de filtre ou de page, comme la liste
  // des demandes de financement : la saisie d'une immatriculation est frappée
  // caractère par caractère.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchDocuments, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchDocuments]);

  // Un changement de filtre change le périmètre visible : garder une sélection
  // faite sur l'ancien la ferait porter sur des documents devenus invisibles.
  const updateFilter = useCallback((key, value) => {
    setPage(1);
    setSelection([]);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setPage(1);
    setSelection([]);
    setFilters(initialFilters);
  }, []);

  const toggleSelection = useCallback((uuid) => {
    setSelection((prev) =>
      prev.includes(uuid)
        ? prev.filter((item) => item !== uuid)
        : [...prev, uuid],
    );
  }, []);

  // Ne porte que sur la page courante : cocher au-delà supposerait de charger
  // toutes les pages, alors que l'archive sans sélection couvre déjà
  // l'intégralité du résultat filtré.
  const togglePageEntiere = useCallback(() => {
    setSelection((prev) => {
      const uuidsPage = documents.map((doc) => doc.uuid);
      const toutSelectionne = uuidsPage.every((uuid) => prev.includes(uuid));
      return toutSelectionne
        ? prev.filter((uuid) => !uuidsPage.includes(uuid))
        : [...prev, ...uuidsPage.filter((uuid) => !prev.includes(uuid))];
    });
  }, [documents]);

  /**
   * Sans sélection, l'archive porte sur l'intégralité du résultat filtré — pas
   * sur la page affichée. Le refus au-delà de 100 fichiers / 200 Mo vient de
   * l'API (422) et son message invite à affiner les filtres.
   */
  const telechargerArchive = useCallback(async () => {
    setIsArchiving(true);
    try {
      const res = await documentsApiService.telechargerArchive(
        documentsAdapters.toApi.payloadArchive(filtresEffectifs, selection),
      );
      FileDownload(res.data, documentsAdapters.nomFichierArchive(res.headers));
    } catch (err) {
      toast.error(await documentsAdapters.messageErreurArchive(err));
    } finally {
      setIsArchiving(false);
    }
  }, [filtresEffectifs, selection]);

  return {
    filters,
    updateFilter,
    resetFilters,
    page,
    setPage,
    documents,
    meta,
    isLoading,
    hasError,
    aDejaCharge,
    // Relance immédiate (bouton « Réessayer » de l'état d'erreur), sans
    // attendre le débounce ni changer de filtre.
    recharger: fetchDocuments,
    selection,
    toggleSelection,
    togglePageEntiere,
    telechargerArchive,
    isArchiving,
    maxSelection: MAX_SELECTION,
  };
};

export default useDocuments;
